import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { WorkshopAPI } from '@chancenwerk/shared/workshops/api/WorkshopAPI.js';
import { WorkshopAttendance } from '@chancenwerk/shared/workshops/entities/WorkshopAttendance.js';
import { WorkshopAttendanceStatus } from '@chancenwerk/shared/workshops/entities/WorkshopAttendanceStatus.js';
import { WorkshopFilter } from '@chancenwerk/shared/workshops/entities/WorkshopFilter.js';
import { WorkshopTrainingAttendance } from '@chancenwerk/shared/workshops/entities/WorkshopTrainingAttendance.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { switchMap, takeUntil } from 'rxjs';
import '../components/akademie-component-workshop-registration.js';

/**
 * @customElement akademie-page-workshop-reg
 */
@customElement('akademie-page-workshop-reg')
export class AkademiePageWorkshops extends CrPage {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  @state() _myAppointments = new RXController(
    this,
    this._currentUser.observable.pipe(
      switchMap(curUser =>
        WorkshopAPI.subscribeAttendanceSimple(
          0,
          0,
          new WorkshopFilter({
            userID: curUser?._id,
          }),
        )({
          ...WorkshopAttendance.allFields,
          appointment: {
            date: 1,
          },
        }),
      ),
    ),
  );
  @state() _workshopRegID = new RXController(this, routingState.searchParams.get('workshopID'));

  constructor() {
    super();

    this._workshopRegID.observable.pipe(takeUntil(this.disconnected)).subscribe(async id => {
      if (!id) {
        return;
      }
      await ChromiumDialog.showPopup({
        content: resolve => {
          return html`<akademie-component-workshop-registration
            .workshopID=${id}
            @success=${resolve}
          ></akademie-component-workshop-registration>`;
        },
      });
    });

    this.label = 'Workshop Anmeldungen';
    this.description = '';
  }

  override content() {
    return html`<cr-flex padding="sm">
      <cr-card padding="md" space="md">
        <chancen-component-card-title> Deine Anmeldungen und Teilnahmen </chancen-component-card-title>
        <cr-data-view
          .options=${{
            class: WorkshopTrainingAttendance,
            operation: WorkshopAPI.subscribeAllAttendanceSimple.bind(WorkshopAPI),
            view: new View<typeof WorkshopTrainingAttendance>({
              filter: new WorkshopFilter({
                userID: this._currentUser.value?._id,
              }),
            }),

            onlyShowLastTranslation: true,
            customColumns: [
              {
                columnName: 'Wann',
                fieldName: 'date',
                columnWidth: 'max-content',
              },
              {
                columnName: 'Workshop',
                fieldName: 'workshop.title',
              },
              {
                columnName: 'Status',
                fieldName: 'state',
              },
              {
                columnName: '',
                customID: 'actions',
                selectionSet: { workshop: { appointmentInfo: 1 }, appointment: { _id: 1 } },
                getFieldMapping: (e: WorkshopTrainingAttendance) => {
                  return html`<cr-flex horizontal crossaxis-center space="md" padding="sm">
                    <cr-button
                      icon="info"
                      @click=${async () => {
                        ChromiumDialog.showPopup({
                          modal: true,
                          content: resolve => {
                            return html`<cr-flex padding="md" ${css({ minWidth: '500px' })}>
                              <chancen-component-card-title>
                                Informationen zum Termin

                              <cr-button slot="right" icon="close" mode="icon" @click=${() =>
                                resolve(true)}></cr-button>
                              </chancen-component-card-title>
                              <cr-text>${unsafeHTML(e.workshop?.appointmentInfo)}
                            </cr-flex>`;
                          },
                        });
                      }}
                    >
                      Informationen
                    </cr-button>
                    ${(e?.date ?? DateTime.now()) > DateTime.now() &&
                    e.state !== WorkshopAttendanceStatus.ATTENDED &&
                    e.state !== WorkshopAttendanceStatus.CANCELLED
                      ? html`<cr-button
                          icon="delete"
                          tone="error"
                          @click=${async () => {
                            await ChromiumDialog.guard(
                              async () => {
                                e.state = WorkshopAttendanceStatus.CANCELLED;
                                await e.save();
                                await WorkshopAPI.sendStornoEmail(e.appointment?._id ?? '', e.userID ?? '')();
                              },
                              {
                                hasToConfirm: true,
                                confirmText: 'Möchtest du die Anmeldung wirklich stornieren?',
                              },
                            );
                          }}
                        >
                          Stornieren
                        </cr-button>`
                      : nothing}
                  </cr-flex>`;
                },
              },
            ],
          } as DataViewOptions<typeof WorkshopTrainingAttendance>}
        >
        </cr-data-view>
      </cr-card>
    </cr-flex>`;
  }
}
